/**
 * The drafted fixes on a page (Figma 00, 65:38): `DraftsPanel` is the band at the head of the leaves column,
 * `MechanicalBatch` its first line (how many fixes a rule restores unambiguously, grouped by rule, and OK all),
 * `SurfacedDraft` a word the book never uses with the nearest one it does and a box to change it, and
 * `DraftsNote` the line that says what OK files.
 */
import { useState, type ReactNode } from 'react';
import { SPACE, TEXT, WALK, usePalette } from '../theme';
import { ActionButton } from './action-button';
import { Chip, RAISED } from './chip';
import { Field } from './field';
import { Notice } from './notice';

export function DraftsPanel({ children }: { children: ReactNode }) {
  const p = usePalette();
  return (
    <section
      aria-label="Drafted fixes"
      style={{ display: 'flex', flexDirection: 'column', flexShrink: 0, gap: SPACE.sm, padding: `${SPACE.md}px ${SPACE.lg}px`, borderBottom: `1px solid ${p.hairline}`, maxHeight: WALK.draftsMax, overflowY: 'auto', boxSizing: 'border-box' }}
    >
      {children}
    </section>
  );
}

export function MechanicalBatch({ count, noun, summary, action }: { count: number; noun: string; summary: ReactNode; action: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.snug }}>
      <Chip look={RAISED}>
        {count}
      </Chip>
      <span style={{ fontSize: TEXT.control, color: p.ink, whiteSpace: 'nowrap' }}>{noun}</span>
      <span style={{ flex: 1, minWidth: 0, fontSize: TEXT.control, color: p.inkMuted }}>{summary}</span>
      {action}
    </div>
  );
}

export function SurfacedDraft({ lead, field, note }: { lead: ReactNode; field: ReactNode; note: ReactNode }) {
  const p = usePalette();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.snug }}>
      <span style={{ flex: 1, minWidth: 0, fontSize: TEXT.control, color: p.inkMuted }}>{lead}</span>
      {field}
      <span style={{ flex: 1, minWidth: 0, fontSize: TEXT.control, color: p.inkMuted }}>{note}</span>
    </div>
  );
}

export function DraftsNote({ children }: { children: ReactNode }) {
  const p = usePalette();
  return <p style={{ margin: 0, fontSize: TEXT.caption, color: p.inkMuted, lineHeight: 'normal' }}>{children}</p>;
}

/** The noun the batch's count carries: fixes by a rule, or (with none by rule) the surfaced words to settle. */
function nounOf(mechanical: number): string {
  switch (mechanical) {
    case 0:
      return 'surfaced words to settle on this page';
    case 1:
      return 'mechanical fix on this page';
    default:
      return 'mechanical fixes on this page';
  }
}

/** What the bar needs of the page's drafts: the counts and the surfaced words keyed for the post. */
export interface DraftsBarFacts {
  readonly mechanical: number;
  readonly total: number;
  readonly surfaced: readonly { readonly key: string; readonly word: string; readonly nearest: readonly string[] }[];
}

/** The act OK all: filing the drafts resolves to the sentence of its failure, or to nothing when it was filed. */
export interface FilesDrafts {
  readonly isPending: boolean;
  readonly file: (chosen: Record<string, string>) => Promise<string | undefined>;
}

/**
 * The page's drafted fixes, whole (Figma 00): the batch line with its rules and OK all, a box for each surfaced word,
 * the failure when filing refused, and an optional closing note. Both screens that show a page's drafts draw this one.
 */
export function PageDraftsBar({ bar, summary, okAll, note }: { bar: DraftsBarFacts; summary: ReactNode; okAll: FilesDrafts; note?: ReactNode }) {
  const [chosen, setChosen] = useState<Record<string, string>>({});
  const [said, setSaid] = useState<string | undefined>(undefined);
  if (bar.total === 0) return null;

  async function fileDrafts() {
    if (okAll.isPending) return;
    const picked: Record<string, string> = {};
    for (const row of bar.surfaced) picked[row.key] = chosen[row.key] ?? row.nearest[0] ?? row.word;
    setSaid(undefined);
    const failure = await okAll.file(picked);
    if (failure === undefined) setChosen({});
    else setSaid(failure);
  }

  return (
    <DraftsPanel>
      <MechanicalBatch
        count={bar.mechanical > 0 ? bar.mechanical : bar.surfaced.length}
        noun={nounOf(bar.mechanical)}
        summary={summary}
        action={
          <ActionButton variant="outline" size="compact" isPending={okAll.isPending} onPress={() => void fileDrafts()}>
            {`OK all ${bar.total}`}
          </ActionButton>
        }
      />
      {bar.surfaced.map((row) => (
        <SurfacedDraft
          key={row.key}
          lead={`surfaced \u00b7 \u201c${row.word}\u201d \u2192`}
          field={
            <Field
              look="word"
              labelHidden
              label={`Word for ${row.word}`}
              value={chosen[row.key] ?? row.nearest[0] ?? row.word}
              onChange={(text) => setChosen((held) => ({ ...held, [row.key]: text }))}
            />
          }
          note="nearest the book uses; change it, or put the word back to keep it"
        />
      ))}
      {said !== undefined && <Notice kind="error">{said}</Notice>}
      {note !== undefined && <DraftsNote>{note}</DraftsNote>}
    </DraftsPanel>
  );
}
